import { useState, useEffect, useCallback } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';

// Pages
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Dashboard from '@/pages/Dashboard';
import Orders from '@/pages/Orders';
import CreateOrder from '@/pages/CreateOrder';
import Dispensaries from '@/pages/Dispensaries';
import Riders from '@/pages/Riders';
import Refunds from '@/pages/Refunds';
import OrderTracking from '@/pages/OrderTracking';
import QRGenerator from '@/pages/QRGenerator';
import QRScanner from '@/pages/QRScanner';
import Profile from '@/pages/Profile';
import AdminPanel from '@/pages/AdminPanel';
import RouteOptimization from '@/pages/RouteOptimization';
import RouteManager from '@/pages/RouteManager';
import RiderDashboard from '@/pages/RiderDashboard';
import PublicOrderTracking from '@/pages/PublicOrderTracking';
import OrderTrackingRealTime from '@/pages/OrderTrackingRealTime';
import Analytics from '@/pages/Analytics';
import PulseDashboard from '@/pages/PulseDashboard';

// Components
import InstallPrompt from '@/components/InstallPrompt';
import IOSInstallPrompt from '@/components/IOSInstallPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios defaults - use credentials for httpOnly cookies
axios.defaults.baseURL = API;
axios.defaults.withCredentials = true;

// Axios interceptor for token refresh on 401 (only for authenticated routes)
let isRefreshing = false;
let hasEverLoggedIn = false;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';
    
    // NEVER trigger logout flow for auth endpoints
    if (requestUrl.includes('/auth/')) {
      return Promise.reject(error);
    }
    
    // For other 401s, only attempt refresh if user was logged in before
    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshing && hasEverLoggedIn) {
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        await axios.post('/auth/refresh');
        isRefreshing = false;
        return axios(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    toast.info('Sesión cerrada');
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get('/auth/me');
        setUser(response.data);
        // User has a valid session, mark as logged in
        hasEverLoggedIn = true;
      } catch (error) {
        // Silent fail - user just needs to login
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
    
    // Listen for forced logout events (e.g., token refresh failure)
    const handleForcedLogout = () => {
      setUser(null);
      toast.error('Sesión expirada. Por favor inicia sesión nuevamente.');
    };
    
    window.addEventListener('auth:logout', handleForcedLogout);
    return () => window.removeEventListener('auth:logout', handleForcedLogout);
  }, []);

  const handleLogin = (userData) => {
    // Mark that user has logged in at least once
    hasEverLoggedIn = true;
    // No need to store token - it's in httpOnly cookie
    setUser(userData);
    toast.success('¡Bienvenido a UID!');
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F5DC' }}>
        <div className="text-xl" style={{ color: '#0E2A24' }}>Cargando...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes 
          user={user} 
          handleLogout={handleLogout} 
          handleLogin={handleLogin}
          refreshUser={refreshUser}
        />
      </BrowserRouter>
      <InstallPrompt />
      <IOSInstallPrompt />
      <Toaster position="top-right" />
    </div>
  );
}

/**
 * AppRoutes - Handles routing with OAuth callback detection
 * CRITICAL: Check for session_id in hash SYNCHRONOUSLY during render
 * to prevent race conditions with ProtectedRoute checks
 */
function AppRoutes({ user, handleLogout, handleLogin, refreshUser }) {
  const location = useLocation();
  
  // Check URL fragment for session_id SYNCHRONOUSLY (not in useEffect)
  // This prevents race conditions by processing OAuth callback FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback onLogin={handleLogin} />;
  }
  
  // Handle explicit /auth/callback route as well
  if (location.pathname === '/auth/callback') {
    return <AuthCallback onLogin={handleLogin} />;
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <Login onLogin={handleLogin} />} />
      <Route path="/auth/callback" element={<AuthCallback onLogin={handleLogin} />} />
      <Route path="/" element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/orders" element={user ? <Orders user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/orders/create" element={user ? <CreateOrder user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/orders/:orderId" element={user ? <OrderTracking user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/dispensaries" element={user ? <Dispensaries user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/riders" element={user ? <Riders user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/refunds" element={user ? <Refunds user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/qr-generator" element={user ? <QRGenerator user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/qr-scanner" element={user ? <QRScanner user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/profile" element={user ? <Profile user={user} onLogout={handleLogout} onUserUpdate={refreshUser} /> : <Navigate to="/login" />} />
      <Route path="/route-optimization" element={user ? <RouteOptimization user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/route-manager" element={user && (user.role === 'admin' || user.role === 'support') ? <RouteManager user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/rider-dashboard" element={user && user.role === 'rider' ? <RiderDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/admin" element={user && (user.role === 'admin' || user.role === 'support') ? <AdminPanel user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/analytics" element={user && ['admin', 'support', 'dispensary_owner', 'dispensary_supervisor'].includes(user.role) ? <Analytics user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/pulse" element={user && ['admin', 'support'].includes(user.role) ? <PulseDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      
      {/* Public tracking route - no auth required */}
      <Route path="/track/:orderId" element={<PublicOrderTracking />} />
      
      {/* Real-time tracking for authenticated users */}
      <Route path="/tracking/:orderId" element={user ? <OrderTrackingRealTime user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
    </Routes>
  );
}

export default App;